import dayjs, { Dayjs } from 'dayjs';
import { ChartData, ChartDataset } from 'chart.js';
import { compressPerSymbolForPriceMap } from '@/modules/calendar/components/data/impact-charting/chart-data-utils';
import { SYMBOL_COLORS } from '@/modules/calendar/model/data/data-impact-symbol-sets';
import { SymbolPriceData } from '@/modules/calendar/model/data/data-impact-chart';

type DayjsWithTimezone = typeof dayjs & {
  tz?: (date: string, timezone: string) => Dayjs;
};

interface ScaleConfig {
  type: 'linear' | 'time' | 'timeseries' | 'category' | 'logarithmic';
  display: boolean;
  position: 'left' | 'right' | 'top' | 'bottom';
  grid: { display: boolean };
  ticks: { color: string; display?: boolean };
  title: { display: boolean };
  overlaying?: string;
}

/**
 * Parse a date string with timezone support
 */
function parseDateWithTimezone(dateStr: string, timezone = 'America/New_York'): Dayjs | null {
  const dayjsWithTz = dayjs as DayjsWithTimezone;
  return dayjsWithTz.tz(dateStr, timezone);
}

/**
 * Parse a date string and return milliseconds timestamp
 * Returns undefined if parsing fails
 */
function parseDateToMs(dateStr: string, timezone = 'America/New_York'): number | undefined {
  const parsed = parseDateWithTimezone(dateStr, timezone);
  return parsed ? parsed.valueOf() : undefined;
}

export function payloadToChartData(payload: SymbolPriceData[], eventDate?: string) {
  // collect timestamps
  const tsSet = new Set<number>();
  payload.forEach(item => {
    (item.prices || []).forEach(p => tsSet.add(p.timestamp));
  });

  const priceMaps: Map<number, number>[] = payload.map(item => {
    const m = new Map<number, number>();
    (item.prices || []).forEach(p => m.set(p.timestamp, p.open));
    return m;
  });

  // Determine event center (ms)
  let eventMs: number | undefined;
  if (eventDate) {
    eventMs = parseDateToMs(eventDate);
  }

  const perSymbolSelected: number[][] = (priceMaps || []).map((pm, idx) => {
    const symbolName = payload[idx]?.symbol;
    return eventMs
      ? compressPerSymbolForPriceMap(pm, eventMs, symbolName)
      : Array.from(pm.keys()).sort((a, b) => a - b);
  });

  const unionSet = new Set<number>();
  perSymbolSelected.forEach(arr => arr.forEach(v => unionSet.add(v)));
  const unionSelectedTimestamps = Array.from(unionSet).sort((a, b) => a - b);

  const symbolColors = Array.from(SYMBOL_COLORS) as string[];

  const datasets: ChartDataset<'line'>[] = payload.map((item, idx) => {
    const priceMap = priceMaps[idx];
    const selected = perSymbolSelected[idx] || [];

    const dataArr = selected.map(ts => {
      const v = priceMap.get(ts);
      return v !== undefined && v !== null
        ? { x: ts, y: Math.round(v * 100) / 100 }
        : { x: ts, y: null };
    });

    return {
      label: item.symbol,
      data: dataArr,
      borderColor: symbolColors[idx % symbolColors.length],
      backgroundColor: symbolColors[idx % symbolColors.length],
      tension: 0.2,
      spanGaps: true,
      parsing: { xAxisKey: 'x', yAxisKey: 'y' },
    } as ChartDataset<'line'>;
  });

  const scales: Record<string, ScaleConfig> = {};
  datasets.forEach((ds, idx) => {
    const axisId = idx === 0 ? 'y' : `y${idx}`;
    (ds as any).yAxisID = axisId;
    const side = idx % 2 === 0 ? 'left' : 'right';
    const color = (ds.borderColor as string) || symbolColors[idx % symbolColors.length];
    scales[axisId] = {
      type: 'linear',
      display: true,
      position: side,
      grid: { display: idx === 0 },
      ticks: { color, display: false },
      title: { display: false },
      ...(idx === 0 ? {} : { overlaying: 'y' }),
    };
  });

  const chartData: ChartData<'line'> = {
    labels: unionSelectedTimestamps,
    datasets,
  };

  const gridColorVal =
    typeof document !== 'undefined'
      ? getComputedStyle(document.body).getPropertyValue('--bs-border-color') || 'grey'
      : 'grey';

  const baseChartOptions = {
    scales: {
      x: {
        type: 'time' as const,
        time: {
          parser: (v: any) => {
            if (typeof v === 'number') {
              return v;
            }
            const ms = parseDateToMs(String(v));
            return ms ?? dayjs(String(v)).valueOf();
          },
          tooltipFormat: 'YYYY-MM-DD HH:mm:ss',
        },
        grid: { color: gridColorVal },
      },
      ...scales,
    },
  };

  const mergedScales = {
    ...(baseChartOptions.scales || {}),
    ...scales,
    x: {
      ...(baseChartOptions.scales?.x || {}),
      ...(scales.x || {}),
      grid: { color: gridColorVal },
    },
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    scales: mergedScales,
    plugins: {
      legend: {
        position: 'bottom' as const,
        labels: { boxWidth: 12, padding: 8, usePointStyle: false, font: { size: 12 } },
      },
      title: { display: true, text: 'Top Symbols Impacted by Event' },
      tooltip: {
        mode: 'x' as const,
        intersect: false,
        position: 'nearest' as const,
        backgroundColor: 'rgba(0,0,0,0.8)',
        borderColor: 'rgba(255,255,255,0.2)',
        borderWidth: 1,
        padding: 8,
        cornerRadius: 4,
        titleFont: { size: 12, weight: 'bold' as const },
        bodyFont: { size: 11 },
        displayColors: true,
        boxPadding: 6,
        filter: function (item: any, index: number, items: any[], data: any) {
          // Filter so only one item per symbol shows up
          const symbolLabel = item.dataset.label || '';

          // Check if this symbol has already appeared in items before this one
          for (let i = 0; i < index; i++) {
            const earlier = items[i];
            if (earlier && earlier.dataset.label === symbolLabel) {
              // This symbol already appeared earlier, filter it out
              return false;
            }
          }

          return true;
        },
      },
      ticks: { color: gridColorVal },
      layout: { padding: { top: 8, bottom: 8 } },
      vlinePlugin: { label: eventMs ?? eventDate, color: 'red', width: 2 },
    },
  };

  return { chartData, chartOptions };
}
